// Keeps FB Studio useful while staff switch apps or close the tab: server-pushed "ready" notifications (service worker +
// Web Push) and AI calls that survive a dropped connection without spending a second AI turn.

const SW_URL = '/fb-sw.js';
const SW_SCOPE = '/fb';
const PENDING_KEY = 'fbStudioAssist';
// The server gives one AI call up to 240s (function limit 300s); past that the answer is not coming.
const ASSIST_MAX_MS = 5.5 * 60 * 1000;

export const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
export const isStandalone = () => window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;
const pushSupported = () => 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

/** 'ios-install' (iPhone must add the app to the home screen first) | 'unsupported' | 'default' | 'denied' | 'granted' */
export function notifyState() {
    if (isIos() && !isStandalone()) return 'ios-install';
    if (!pushSupported()) return 'unsupported';
    return Notification.permission;
}

/** Home-screen install (needed for notifications on iPhone): manifest + icon, added only while FB Studio is open. */
export function addManifest() {
    const add = (tag, attrs) => {
        if (document.head.querySelector(`${tag}[data-fb="${attrs.rel || attrs.name}"]`)) return;
        const el = document.createElement(tag);
        Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
        el.setAttribute('data-fb', attrs.rel || attrs.name);
        document.head.appendChild(el);
    };
    add('link', { rel: 'manifest', href: '/fb-manifest.json' });
    add('link', { rel: 'apple-touch-icon', href: '/fb-icon-192.png' });
    add('meta', { name: 'apple-mobile-web-app-capable', content: 'yes' });
    add('meta', { name: 'apple-mobile-web-app-title', content: 'FB Studio' });
}

export async function ensureWorker() {
    if (!('serviceWorker' in navigator)) return null;
    try {
        await navigator.serviceWorker.register(SW_URL, { scope: SW_SCOPE });
        return await navigator.serviceWorker.ready;
    } catch (_) {
        return null;
    }
}

const keyBytes = base64 => {
    const pad = '='.repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + pad).replace(/-/g, '+').replace(/_/g, '/'));
    return Uint8Array.from(raw, c => c.charCodeAt(0));
};

/**
 * Turns on server notifications for this browser. ask=false only refreshes an existing permission (no prompt).
 * → the permission state after the call
 */
export async function enablePush(api, { ask = true } = {}) {
    if (!pushSupported()) return notifyState();
    const perm = ask ? await Notification.requestPermission() : Notification.permission;
    if (perm !== 'granted') return perm;
    const reg = await ensureWorker();
    if (!reg) return 'unsupported';
    const { publicKey } = await api('push-key');
    let sub = await reg.pushManager.getSubscription();
    if (sub && sub.options?.applicationServerKey) {
        const current = new Uint8Array(sub.options.applicationServerKey);
        const want = keyBytes(publicKey);
        if (current.length !== want.length || current.some((b, i) => b !== want[i])) {
            await sub.unsubscribe().catch(() => {});
            sub = null;
        }
    }
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(publicKey) });
    await api('push-subscribe', { subscription: sub.toJSON(), device: navigator.userAgent.slice(0, 120) });
    return 'granted';
}

/** A notification from the page itself (Android Chrome only allows them through the service worker). */
export async function showLocal(title, body, tag) {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    const reg = await ensureWorker();
    try {
        if (reg) await reg.showNotification(title, { body, tag, icon: '/fb-icon-192.png', data: { url: '/fb/library' } });
        else new Notification(title, { body, tag, icon: '/fb-icon-192.png' });
    } catch (_) { /* notifications are optional */ }
}

// ---------- AI calls that survive switching apps ----------

const sleep = ms => new Promise(r => setTimeout(r, ms));
const whenVisible = () => (document.hidden
    ? new Promise(resolve => {
        const back = () => {
            if (document.hidden) return;
            document.removeEventListener('visibilitychange', back);
            resolve();
        };
        document.addEventListener('visibilitychange', back);
    })
    : Promise.resolve());

const tooSlow = () => new Error('AI trả lời quá lâu — bấm lại để thử.');

/**
 * Asks (again) for one AI job by its request id until the server has the answer. The deadline counts from when the
 * job started (not from this call), so reopening the tab never extends it, and a request that hangs is cut off.
 * options.cancelled() lets the caller stop waiting.
 */
export async function resumeAssist(api, job, { cancelled = () => false } = {}) {
    const until = (job.at || Date.now()) + ASSIST_MAX_MS;
    const left = () => until - Date.now();
    const withDeadline = promise => new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(tooSlow()), Math.max(0, left()));
        promise.then(resolve, reject).finally(() => clearTimeout(timer));
    });
    for (;;) {
        if (cancelled()) throw new Error('Đã hủy.');
        if (left() <= 0) throw tooSlow();
        try {
            const r = await withDeadline(api('assist', { mode: job.mode, input: job.input, masterId: job.masterId, rid: job.rid }));
            if (!r.pending) return r.result || {};
        } catch (error) {
            // fetch() throws TypeError when the phone dropped the connection (app switched, screen off, signal lost)
            if (!(error instanceof TypeError) || left() <= 0) throw error;
            await whenVisible();
            await sleep(1500);
        }
    }
}

/** One AI call, remembered until it finishes so a closed tab can pick the answer up when reopened. */
export async function callAssist(api, { mode, input, masterId }) {
    const job = { rid: `r${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`, mode, input, masterId, at: Date.now() };
    try {
        localStorage.setItem(PENDING_KEY, JSON.stringify(job));
    } catch (_) { /* private mode */ }
    try {
        return await resumeAssist(api, job);
    } finally {
        localStorage.removeItem(PENDING_KEY);
    }
}

export const clearPendingAssist = () => localStorage.removeItem(PENDING_KEY);

/** The AI call left running when the tab was closed (still within its deadline), or null. */
export function takePendingAssist() {
    try {
        const job = JSON.parse(localStorage.getItem(PENDING_KEY) || 'null');
        if (job?.rid && Date.now() - job.at < ASSIST_MAX_MS) return job;
    } catch (_) { /* ignore */ }
    localStorage.removeItem(PENDING_KEY);
    return null;
}
